import "@constructorfleet/ui/design-system/tokens.css";
import "@constructorfleet/ui/design-system/components.css";
import "@constructorfleet/ui/design-system/site.css";
import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import App from "./App";
import { LinkProvider } from "@constructorfleet/ui/design-system";
import { RouterDsLink } from "./ui/ds-link";
import { AuthProvider } from "./context/auth-context";
import { OfferingsProvider } from "./offerings/context";
import { SubscriptionsProvider } from "./context/subscriptions-context";
import { BillingProvider } from "./context/billing";
import { captureReferral } from "./lib/referral";
import { installApiFetchPatch } from "./lib/api-origin";
import { installAnalytics } from "./lib/analytics";
import { shouldEnableWebsiteObservability } from "./observability-gate";

captureReferral();
installApiFetchPatch();
installAnalytics();

const container = document.getElementById("root")!;

const tree = (
  <StrictMode>
    <HelmetProvider>
      <BrowserRouter>
        <LinkProvider component={RouterDsLink}>
          <AuthProvider>
            <BillingProvider>
              <OfferingsProvider>
                <SubscriptionsProvider>
                  <App />
                </SubscriptionsProvider>
              </OfferingsProvider>
            </BillingProvider>
          </AuthProvider>
        </LinkProvider>
      </BrowserRouter>
    </HelmetProvider>
  </StrictMode>
);

// Prerendered routes (scripts/prerender.ts) ship full markup inside #root —
// hydrate it so React adopts that DOM instead of discarding and re-rendering it.
// That adoption is what removes the footer layout shift (CLS): a lazy() route
// that suspends during hydration keeps its prerendered content on screen until
// the chunk loads, rather than collapsing to the Suspense fallback and back.
// The un-prerendered SPA shell (dev, or index.html served directly) has an empty
// #root, where hydration would only produce mismatches — fall back to createRoot.
if (container.hasChildNodes()) {
  hydrateRoot(container, tree);
} else {
  createRoot(container).render(tree);
}

// Load and initialize Sentry off the critical path. Importing it statically
// pulls the whole @sentry/react graph into the entry bundle and blocks first
// paint; deferring keeps it out of the initial download (PageSpeed "reduce
// unused JavaScript") while still capturing errors moments after mount.
if (
  shouldEnableWebsiteObservability({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    observabilityEnabledRaw: import.meta.env.VITE_ENABLE_OBSERVABILITY,
  })
) {
  void import("./sentry");
}
